import {MapInitOptions} from '../interfaces';
import {MapLayersController} from '../controllers/map-layers.controller';
import {MapDrawingController} from '../controllers/draw/map-drawing.controller';
import {GeoFileLoaderManager} from './geo-file-loader.manager';
import {
  isMapPolygonFillVisible,
  setMapPolygonFillVisible,
  OSM_POLYGON_FILL_VISIBILITY_EVENT,
} from '../constants/map-polygon-fill-visibility.state';

export interface VisibilityManagerAttachContext {
  mapOptions: MapInitOptions;
  getLayersController: () => MapLayersController | undefined;
  getDrawingController: () => MapDrawingController | undefined;
  getMapInstance: () => any;
  geoFileLoaderManager: GeoFileLoaderManager;
}

/**
 * Map visibility UI state: region labels and polygon fill (Leaflet paths, canvas VT, uploaded GeoJSON).
 */
export class VisibilityManager {
  menuOpen = false;
  /** Mirrors {@link MapInitOptions.showRegionNames}; default checked when option is not explicitly false. */
  showRegionNames = true;
  /** When false, polygons render as outlines only (dynamic layers, drawings, geo file layers, VT fields/seasons). */
  polygonFillVisible = true;

  private ctx: VisibilityManagerAttachContext | null = null;

  attach(ctx: VisibilityManagerAttachContext): void {
    this.ctx = ctx;
    this.showRegionNames = ctx.mapOptions.showRegionNames !== false;
    this.polygonFillVisible = isMapPolygonFillVisible();
    setMapPolygonFillVisible(this.polygonFillVisible);
    this.applyShowRegionNames();
    this.applyPolygonFill();
  }

  /** Keep the eye-menu checkbox aligned when `mapController$` sets region names. */
  syncShowRegionNamesFromExternal(value: boolean): void {
    this.showRegionNames = value;
    if (this.ctx?.mapOptions) {
      this.ctx.mapOptions.showRegionNames = value;
    }
  }

  toggleMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.menuOpen = !this.menuOpen;
  }

  closeMenuUnlessTargetInside(target: EventTarget | null, rootSelector: string): void {
    if (!this.menuOpen) {
      return;
    }
    const el = target as Node | null;
    if (el && typeof (el as any).closest === 'function' && (el as Element).closest(rootSelector)) {
      return;
    }
    this.menuOpen = false;
  }

  setShowRegionNames(checked: boolean): void {
    this.showRegionNames = checked;
    if (this.ctx?.mapOptions) {
      this.ctx.mapOptions.showRegionNames = checked;
    }
    this.applyShowRegionNames();
  }

  setPolygonFillVisible(checked: boolean): void {
    this.polygonFillVisible = checked;
    setMapPolygonFillVisible(checked);
    this.applyPolygonFill();
  }

  private applyShowRegionNames(): void {
    const layers = this.ctx?.getLayersController?.();
    layers?.setShowRegionNames(this.showRegionNames);
  }

  private applyPolygonFill(): void {
    const fill = this.polygonFillVisible;
    const layers = this.ctx?.getLayersController?.();
    const draw = this.ctx?.getDrawingController?.();
    const map = this.ctx?.getMapInstance?.();
    const geo = this.ctx?.geoFileLoaderManager;

    layers?.applyPolygonFillVisibility?.(fill);
    draw?.applyPolygonFillVisibility?.(fill);
    geo?.redrawForPolygonFillVisibility?.();

    try {
      map?.fire?.(OSM_POLYGON_FILL_VISIBILITY_EVENT, {fillVisible: fill});
    } catch {
      /* ignore */
    }
  }
}
